import { apiUrl } from "@/lib/api-base";

export type AuthApiResult = { ok: true } | { ok: false; error: string };

async function parseError(res: Response, fallback: string): Promise<string> {
  if (res.status === 429) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    return body?.error ?? "Too many attempts. Please wait a while before trying again.";
  }
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return body?.error ?? fallback;
}

export type ForgotPasswordResult =
  | { status: "sent" }
  | { status: "not_found" }
  | { status: "no_password" }
  | { status: "error"; error: string };

/**
 * Requests a password reset email. The backend reveals whether the address is
 * registered and, if so, whether it even has a password to reset (product
 * choice — trades user-enumeration resistance for clearer UX).
 */
export async function requestPasswordReset(email: string): Promise<ForgotPasswordResult> {
  try {
    const res = await fetch(apiUrl("/auth/forgot-password"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      return { status: "error", error: await parseError(res, "Something went wrong. Please try again.") };
    }
    const body = (await res.json()) as { sent?: boolean; reason?: "not_found" | "no_password" };
    // Check `reason` before `sent` — a Google-only account now also gets `sent: true`
    // (we email them a set-password link), but the frontend still needs to know it's
    // that specific case so it can show the right copy and the Google fallback button.
    if (body.reason === "not_found") return { status: "not_found" };
    if (body.reason === "no_password") return { status: "no_password" };
    return { status: body.sent ? "sent" : "not_found" };
  } catch {
    return { status: "error", error: "Network error — please check your connection and try again." };
  }
}

export async function resetPassword(token: string, password: string): Promise<AuthApiResult> {
  try {
    const res = await fetch(apiUrl("/auth/reset-password"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!res.ok) {
      return { ok: false, error: await parseError(res, "This reset link is invalid or has expired.") };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "Network error — please check your connection and try again." };
  }
}
